/**
 * Modal: conta Google Ads + ações de conversão (multi-select, inclusive tag global).
 */
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import type { GoogleAdsConversionAction, GoogleAdsCustomerOption } from '../../../services/modules/googleAdsRepository';
import GoogleAdsConversionPicker from './GoogleAdsConversionPicker';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    customers: GoogleAdsCustomerOption[];
    actions: GoogleAdsConversionAction[];
    loadingActions: boolean;
    isSaving: boolean;
    onCustomerChange: (customer: GoogleAdsCustomerOption) => void;
    onSave: (input: {
        customerId: string;
        customerName: string;
        conversionActionIds: string[];
        loginCustomerId?: string;
    }) => void;
};

function formatCustomerLabel(c: GoogleAdsCustomerOption): string {
    const name = c.name && c.name !== c.customerId ? c.name : `Conta ${c.customerId}`;
    const suffix = c.isManager ? ' · MCC' : '';
    return `${name} (${c.customerId})${suffix}`;
}

const GoogleAdsConnectModal: React.FC<Props> = ({
    isOpen,
    onClose,
    customers,
    actions,
    loadingActions,
    isSaving,
    onCustomerChange,
    onSave,
}) => {
    const [customerId, setCustomerId] = useState('');
    const [actionIds, setActionIds] = useState<string[]>([]);

    useEffect(() => {
        if (!isOpen) {
            setCustomerId('');
            setActionIds([]);
        }
    }, [isOpen]);

    const customerOptions = useMemo(
        () => customers.map((c) => ({ value: c.customerId, label: formatCustomerLabel(c) })),
        [customers]
    );

    const selectedCustomer = useMemo(
        () => customers.find((c) => c.customerId === customerId),
        [customers, customerId]
    );

    const canSave = Boolean(customerId && actionIds.length > 0);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <Modal.Header onClose={onClose}>
                Configurar Google Ads
            </Modal.Header>
            <Modal.Body>
                <div className="flex min-w-0 flex-col gap-s4">
                    <p className="text-body text-secondary">
                        Inclui tag global (site) e importação. No ganho, enviamos a venda
                        para todas as selecionadas.
                    </p>
                    <Select
                        label="Conta Google Ads *"
                        value={customerId}
                        options={customerOptions}
                        containerClassName="min-w-0 w-full"
                        className="min-h-11 w-full min-w-0 md:min-h-9"
                        onChange={(e) => {
                            const id = e.target.value;
                            setCustomerId(id);
                            setActionIds([]);
                            const customer = customers.find((c) => c.customerId === id);
                            if (customer) onCustomerChange(customer);
                        }}
                        placeholder="Selecione a conta"
                    />
                    <GoogleAdsConversionPicker
                        actions={actions}
                        selectedIds={actionIds}
                        loading={loadingActions}
                        disabled={!customerId || loadingActions}
                        onToggle={(id) => setActionIds((prev) => (
                            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                        ))}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSaving}
                    className="min-h-11 w-full sm:w-auto"
                >
                    Cancelar
                </Button>
                <Button
                    onClick={() => {
                        if (!selectedCustomer || !canSave) return;
                        onSave({
                            customerId: selectedCustomer.customerId,
                            customerName: selectedCustomer.name,
                            conversionActionIds: actionIds,
                            loginCustomerId: selectedCustomer.loginCustomerId,
                        });
                    }}
                    disabled={!canSave || isSaving}
                    className="min-h-11 w-full sm:w-auto"
                >
                    {isSaving ? 'Salvando…' : 'Salvar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GoogleAdsConnectModal;
